import React from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip
} from '@material-ui/core';
import { Delete, Edit } from '@material-ui/icons';
import Button from 'components/CustomButtons/Button';
import GridContainer from 'components/Grid/GridContainer.jsx';
import GridItem from 'components/Grid/GridItem.jsx';
import NotificationAsk from 'components/Notification/NotificationAsk';
import GCIConditionDialog from './GCIConditionDialog';
import {
  comparisonOperatorLabels,
  gci_exception_conditions,
  getConditionValueLabel
} from './GCIExceptionsLogic';

const getInitialRule = entry => {
  const rule = entry && entry.rule_json && typeof entry.rule_json === 'object'
    ? entry.rule_json
    : {};
  const split = Number(rule.gci_split);

  return {
    title: entry && entry.title ? entry.title : '',
    conditions: Array.isArray(rule.conditions) ? rule.conditions.map(condition => ({ ...condition })) : [],
    gciSplitPercent: Number.isFinite(split) ? String(split * 100) : ''
  };
};

class GCIExceptionFormDialog extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      ...getInitialRule(props.entry),
      conditionDialogOpen: false,
      editingConditionIndex: null,
      deletingConditionIndex: null
    };
  }

  componentDidUpdate(prevProps) {
    if ((!prevProps.open && this.props.open) || prevProps.entry !== this.props.entry) {
      this.setState({
        ...getInitialRule(this.props.entry),
        conditionDialogOpen: false,
        editingConditionIndex: null,
        deletingConditionIndex: null
      });
    }
  }

  saveCondition = condition => {
    this.setState(state => {
      const conditions = state.conditions.slice();
      if (state.editingConditionIndex === null) {
        conditions.push(condition);
      } else {
        conditions[state.editingConditionIndex] = condition;
      }
      return {
        conditions,
        conditionDialogOpen: false,
        editingConditionIndex: null
      };
    });
  };

  setGciSplitPercent = value => {
    if (value === '') {
      this.setState({ gciSplitPercent: '' });
      return;
    }

    const number = Number(value);
    if (Number.isNaN(number) || number < 0 || number > 100) return;
    this.setState({ gciSplitPercent: value });
  };

  render() {
    const { open, user, entry, saving, onClose, onSave } = this.props;
    if (!user) return null;

    const {
      title,
      conditions,
      gciSplitPercent,
      conditionDialogOpen,
      editingConditionIndex,
      deletingConditionIndex
    } = this.state;
    const splitNumber = Number(gciSplitPercent);
    const canSave = title.trim() !== ''
      && conditions.length > 0
      && gciSplitPercent !== ''
      && !Number.isNaN(splitNumber)
      && splitNumber >= 0
      && splitNumber <= 100
      && !saving;
    const editingCondition = editingConditionIndex === null
      ? null
      : conditions[editingConditionIndex];
    const houseGciSplit = gciSplitPercent === ''
      ? null
      : Math.round((100 - splitNumber) * 100) / 100;

    return (
      <React.Fragment>
        <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
          <DialogTitle>
            {entry ? 'Edit' : 'Add'} GCI Exception — {user.name}
          </DialogTitle>
          <DialogContent>
            <GridContainer>
              <GridItem xs={12} sm={12} md={12}>
                <div style={{ maxWidth: 500, marginBottom: 20 }}>
                  <TextField
                    autoFocus
                    fullWidth
                    label="Title"
                    value={title}
                    onChange={e => this.setState({ title: e.target.value })}
                    inputProps={{ maxLength: 45 }}
                    helperText={`${title.length}/45 characters`}
                  />
                </div>
              </GridItem>

              {conditions.length > 0 && (
                <GridItem xs={12} sm={12} md={12}>
                  <div style={{ maxWidth: 400, marginBottom: 20 }}>
                    <h3>GCI Split</h3>
                    <TextField
                      fullWidth
                      label="Associate GCI Split"
                      type="number"
                      value={gciSplitPercent}
                      onChange={e => this.setGciSplitPercent(e.target.value)}
                      onKeyDown={e => {
                        if (['e', 'E', '+', '-'].indexOf(e.key) !== -1) e.preventDefault();
                      }}
                      inputProps={{ min: 0, max: 100, step: 0.01 }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>
                      }}
                      helperText="Enter a percentage from 0 to 100. It will be saved as a decimal from 0 to 1."
                    />
                    <div style={{ marginTop: 12, fontSize: 16 }}>
                      <strong>House GCI Split:</strong>{' '}
                      {houseGciSplit === null ? '—' : `${houseGciSplit}%`}
                    </div>
                  </div>
                </GridItem>
              )}

              <GridItem xs={12} sm={12} md={12}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3>Conditions</h3>
                  <Button
                    color="primary"
                    size="sm"
                    onClick={() => this.setState({
                      conditionDialogOpen: true,
                      editingConditionIndex: null
                    })}
                  >
                    Add Condition
                  </Button>
                </div>

                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Field</TableCell>
                      <TableCell>Operator</TableCell>
                      <TableCell>Value</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {conditions.map((condition, index) => {
                      const definition = gci_exception_conditions[condition.field];
                      return (
                        <TableRow key={`${condition.field}-${index}`}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>{definition ? definition.label : condition.field}</TableCell>
                          <TableCell>{comparisonOperatorLabels[condition.operator] || condition.operator}</TableCell>
                          <TableCell>{getConditionValueLabel(condition.field, condition.value)}</TableCell>
                          <TableCell>
                            <Tooltip title="Edit condition">
                              <IconButton onClick={() => this.setState({
                                conditionDialogOpen: true,
                                editingConditionIndex: index
                              })}>
                                <Edit />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete condition">
                              <IconButton onClick={() => this.setState({ deletingConditionIndex: index })}>
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {conditions.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5}>Add at least one condition to configure the GCI split.</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </GridItem>
            </GridContainer>
          </DialogContent>
          <DialogActions>
            <Button color="white" disabled={saving} onClick={onClose}>Cancel</Button>
            <Button
              color="primary"
              disabled={!canSave}
              onClick={() => onSave({
                title: title.trim(),
                rule_json: {
                  conditions,
                  gci_split: splitNumber / 100
                }
              })}
            >
              {saving ? 'Saving...' : 'Save Exception'}
            </Button>
          </DialogActions>
        </Dialog>

        <GCIConditionDialog
          open={conditionDialogOpen}
          condition={editingCondition}
          conditions={conditions}
          onClose={() => this.setState({
            conditionDialogOpen: false,
            editingConditionIndex: null
          })}
          onSave={this.saveCondition}
        />

        {deletingConditionIndex !== null && (
          <NotificationAsk
            open={true}
            title="Delete condition?"
            message="Are you sure you want to delete this condition?"
            close={() => this.setState({ deletingConditionIndex: null })}
            success={() => this.setState(state => ({
              conditions: state.conditions.filter((condition, index) => index !== state.deletingConditionIndex),
              deletingConditionIndex: null
            }))}
          />
        )}
      </React.Fragment>
    );
  }
}

GCIExceptionFormDialog.defaultProps = {
  saving: false
};

export default GCIExceptionFormDialog;
